import { capitalize } from 'lodash';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import OrDivider from '@/components/authForm/OrDivider';
import { ContentWrapper } from '@/components/b2b/shared/styles';
import { StoreButton } from '@/components/ui/StoreButton';
import { useAnalytics } from '@/hooks/analytics';
import { useUserState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import { getCookie } from '@/utils/cookies';
import { CALM_ONE_LINKS } from '@/utils/deeplinks/deeplinks';
import { CALM_AUTH_METHOD, CalmAuthMethods } from '@/utils/privacyCookies';

import { SignupScreenProps } from '../shared/types';
import messages from './messages';
import {
	Container,
	CtaButton,
	Grid,
	OuterContainer,
	StoreButtonsWrapper,
	SuccessHeader,
	SuccessParagraph,
	SuccessText,
	Title,
} from './styles';
import SuccessButton from './SuccessButton';
import SuccessQrCode from './SuccessQrCode';

const Success = ({
	partnerDetails: {
		id: partner_id,
		pageInfo: { messages: partnerMessages, successCTAUrl },
		category,
		user_id_descriptor,
		integration_type,
	},
	isRevampRedempFlow,
}: Pick<SignupScreenProps, 'partnerDetails' | 'isRevampRedempFlow'>) => {
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const user = useUserState();
	const userLoggedInWithFb = getCookie(CALM_AUTH_METHOD) === CalmAuthMethods.Facebook;
	const userLoggedInMethod = getCookie(CALM_AUTH_METHOD);

	useComponentDidMount(() => {
		logEvent({
			eventName: 'Success : Landed',
			eventProps: {
				partner_id,
				integration_type,
				...(category && { partner_type: [category] }),
				...(user_id_descriptor && { user_id_descriptor }),
			},
		});
	});

	if (isRevampRedempFlow) {
		return (
			<OuterContainer data-testid="success-qr-code-container">
				<Container>
					<Title>{formatMessage(messages.successTitle)}</Title>
					<SuccessParagraph>
						{formatMessage(messages.successParagraph, {
							login:
								userLoggedInMethod === CalmAuthMethods.Email || userLoggedInMethod === undefined
									? formatMessage(messages.successLoginWithEmail, {
											bold: (...chunks: ReactNode[]) => <b>{chunks}</b>,
										})
									: formatMessage(messages.successLoginWithThirdPartyApp, {
											bold: (...chunks: ReactNode[]) => <b>{chunks}</b>,
											thirdPartyApp: capitalize(userLoggedInMethod),
										}),
						})}
					</SuccessParagraph>
					<SuccessQrCode />
					<OrDivider />
					<CtaButton fullWidth backgroundColor="blue7" href={CALM_ONE_LINKS.app}>
						{formatMessage(messages.successButton)}
					</CtaButton>
				</Container>
			</OuterContainer>
		);
	}

	return (
		<Grid allowOverflow>
			<ContentWrapper>
				<SuccessHeader>{formatMessage(messages.congrats)}</SuccessHeader>
				<SuccessText>
					{formatMessage(partnerMessages?.successText ?? messages.successText, {
						breakingLine: <p />,
						bold: (...chunks) => <b>{chunks}</b>,
						login: userLoggedInWithFb
							? formatMessage(messages.successTextFacebook)
							: formatMessage(messages.successTextEmail, {
									customerEmail: user?.email,
								}),
					})}
				</SuccessText>
				<StoreButtonsWrapper>
					<StoreButton type="ios" />
					<StoreButton type="android" />
				</StoreButtonsWrapper>
				<SuccessButton successCTAUrl={successCTAUrl} partnerMessages={partnerMessages} />
			</ContentWrapper>
		</Grid>
	);
};

export default Success;
